// src/components/dashboard/AddAccessories.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import CustomAlert from "../CustomAlert";
import { AddIcon } from "../../assets/icons/icons";

// تعقيم كود اللون (#RGB/#RRGGBB)
const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{6}$/i.test(hex)) return `#${hex}`;
  return "";
};

// helper بسيط للتعامل مع اختلاف أسماء المفاتيح إن وجدت
const brandIdOfModel = (m) =>
  String(m?.brand_id ?? m?.brandId ?? m?.brand?.brand_id ?? m?.brand?.id ?? "");

export default function AddAccessories() {
  // Text fields
  const [accessoriesNameE, setAccessoriesNameE] = useState("");
  const [accessoriesNameA, setAccessoriesNameA] = useState("");
  const [accessoriesPrice, setAccessoriesPrice] = useState("");
  const [accessoriesPriceDiscount, setAccessoriesPriceDiscount] = useState("");
  const [accessoriesTag, setAccessoriesTag] = useState("");
  const [accessoriesStockAvailable, setAccessoriesStockAvailable] = useState("");
  const [accessoriesFeaturesE, setAccessoriesFeaturesE] = useState("");
  const [accessoriesFeaturesA, setAccessoriesFeaturesA] = useState("");
  const [accessoriesDescE, setAccessoriesDescE] = useState("");
  const [accessoriesDescA, setAccessoriesDescA] = useState("");

  // Files
  const [accessoriesMainImg, setAccessoriesMainImg] = useState(null);
  const [mainPreview, setMainPreview] = useState(null);
  const [accessoriesImages, setAccessoriesImages] = useState([]);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);

  // API options
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [modelsOptions, setModelsOptions] = useState([]);

  // Selected values
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [selectedBrandId, setSelectedBrandId] = useState(""); // string
  const [selectedBrandName, setSelectedBrandName] = useState("");
  const [selectedModels, setSelectedModels] = useState([]); // array of model_id

  // ألوان ديناميكية (اسم + HEX) — مثل البرودكت
  const [accessoriesColor, setAccessoriesColor] = useState([]); // أسماء الألوان فقط
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#000000");
  const [colorsWithHex, setColorsWithHex] = useState([]); // [{name, hex}]

  // Alert
  const [alert, setAlert] = useState(null);

  // استرجاع تنبيه بعد الريلود
  useEffect(() => {
    const savedAlert = localStorage.getItem("alertMessage");
    if (savedAlert) {
      setAlert(JSON.parse(savedAlert));
      localStorage.removeItem("alertMessage");
    }
  }, []);

  // auth header
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  // Fetch options
  useEffect(() => {
    axios
      .get("https://laravel-api.aj-mart.com/api/accessoriesCategory")
      .then((res) => setCategoriesOptions(res.data.accessories_category || []))
      .catch((err) => console.error("Error fetching categories:", err));

    axios
      .get("https://laravel-api.aj-mart.com/api/brand")
      .then((res) => setBrandsOptions(res.data.brands || []))
      .catch((err) => console.error("Error fetching brands:", err));

    axios
      .get("https://laravel-api.aj-mart.com/api/accessoriesModel")
      .then((res) => setModelsOptions(res.data.models || []))
      .catch((err) => console.error("Error fetching models:", err));
  }, []);

  // Update selected names by id
  useEffect(() => {
    if (selectedCategoryId && categoriesOptions.length > 0) {
      const category = categoriesOptions.find(
        (cat) => String(cat.accessories_category_id) === String(selectedCategoryId)
      );
      if (category) setSelectedCategoryName(category.accessories_category_name_E);
    }
  }, [selectedCategoryId, categoriesOptions]);

  useEffect(() => {
    if (selectedBrandId && brandsOptions.length > 0) {
      const selectedBrand = brandsOptions.find(
        (brand) => String(brand.brand_id) === String(selectedBrandId)
      );
      if (selectedBrand) setSelectedBrandName(selectedBrand.brand_name);
    }
  }, [selectedBrandId, brandsOptions]);

  // فلترة الموديلات حسب البراند المختار
  const filteredModels = useMemo(() => {
    if (!selectedBrandId) return [];
    return (modelsOptions || []).filter((m) => brandIdOfModel(m) === String(selectedBrandId));
  }, [modelsOptions, selectedBrandId]);

  // عند تغيير البراند: نظّف الموديلات من براند آخر
  useEffect(() => {
    if (!selectedBrandId) {
      if (selectedModels.length) setSelectedModels([]);
      return;
    }
    if (!selectedModels.length) return;
    const ids = new Set(filteredModels.map((m) => m.model_id));
    setSelectedModels((prev) => prev.filter((id) => ids.has(id)));
  }, [selectedBrandId, filteredModels, selectedModels.length]);

  // Images handlers
  const handleMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAccessoriesMainImg(file);
      const reader = new FileReader();
      reader.onloadend = () => setMainPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    setAccessoriesImages(files);
    const previews = files.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        })
    );
    Promise.all(previews).then((results) => setAdditionalPreviews(results));
  };

  // ===== ألوان ديناميكية (مثل البرودكت) =====
  const addColor = () => {
    const name = (colorName || "").trim();
    if (!name) {
      setAlert({ type: "error", message: "أدخل اسم اللون أولاً." });
      return;
    }
    const exists = accessoriesColor.some((c) => c.toLowerCase() === name.toLowerCase());
    if (exists) {
      setAlert({ type: "error", message: "هذا اللون مُضاف مسبقًا." });
      return;
    }
    const hex = sanitizeCssColor(colorHex) || "";
    setAccessoriesColor((prev) => [...prev, name]);
    setColorsWithHex((prev) => [...prev, { name, hex }]);
    setColorName("");
  };

  const removeColor = (name) => {
    setAccessoriesColor((prev) => prev.filter((c) => c.toLowerCase() !== name.toLowerCase()));
    setColorsWithHex((prev) => prev.filter((c) => c.name.toLowerCase() !== name.toLowerCase()));
  };

  const updateColorHex = (name, hexVal) => {
    const sanitized = sanitizeCssColor(hexVal) || "";
    setColorsWithHex((prev) =>
      prev.map((c) => (c.name.toLowerCase() === name.toLowerCase() ? { ...c, hex: sanitized } : c))
    );
  };

  const handleColorNameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addColor();
    }
  };

  // Models selection
  const handleModelChange = (e, modelId) => {
    if (e.target.checked) setSelectedModels((prev) => [...prev, modelId]);
    else setSelectedModels((prev) => prev.filter((id) => id !== modelId));
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("accessories_name_E", accessoriesNameE);
    formData.append("accessories_name_A", accessoriesNameA);
    formData.append("accessories_category", selectedCategoryName);
    formData.append("accessories_category_id", selectedCategoryId);
    formData.append("accessories_model", JSON.stringify(selectedModels));
    formData.append("model_id", selectedModels[0] || ""); // primary (اختياري)
    formData.append("accessories_brand_name", selectedBrandName);
    formData.append("accessories_price", accessoriesPrice);
    formData.append("accessories_price_discount", accessoriesPriceDiscount);
    formData.append("accessories_tag", accessoriesTag);
    formData.append("accessories_stock_available", accessoriesStockAvailable);
    formData.append("accessories_username", ""); // املأها لو عندك يوزرنيم

    formData.append("accessories_features_E", accessoriesFeaturesE);
    formData.append("accessories_features_A", accessoriesFeaturesA);
    formData.append("accessories_desc_E", accessoriesDescE);
    formData.append("accessories_desc_A", accessoriesDescA);

    // ✅ إرسال الألوان كسلسلة (string) كما يتوقع الباكند — JSON string للأسماء
    formData.append("accessories_color", JSON.stringify(accessoriesColor));

    // ✅ JSON إضافي (اختياري) يحتوي الاسم + HEX لو حاب تحفظه مستقبلاً
    const jsonColors = (accessoriesColor || []).map((name) => {
      const found = colorsWithHex.find((c) => c.name.toLowerCase() === name.toLowerCase());
      return { name, hex: sanitizeCssColor(found?.hex || "") };
    });
    formData.append("accessories_colors_with_hex", JSON.stringify(jsonColors));

    // ❌ لا ترسل accessories_color[] كمصفوفة — كان سبب الـ 422
    // (accessoriesColor || []).forEach((c) => formData.append("accessories_color[]", c));

    if (accessoriesMainImg) formData.append("accessories_main_img", accessoriesMainImg);
    accessoriesImages.forEach((file, index) => {
      formData.append(`accessories_images[${index}]`, file);
    });
    formData.append("brand_id", selectedBrandId);

    try {
      await axios.post("https://laravel-api.aj-mart.com/api/accessories/add", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const successAlert = { type: "success", message: "Accessory added successfully" };
      localStorage.setItem("alertMessage", JSON.stringify(successAlert));
      window.location.reload();
    } catch (error) {
      console.error("Error adding accessory:", error);
      // إظهار تفاصيل 422 (لو موجودة) للمستخدم
      const firstErr =
        error?.response?.data?.error &&
        Object.values(error.response.data.error)[0] &&
        (Array.isArray(Object.values(error.response.data.error)[0])
          ? Object.values(error.response.data.error)[0][0]
          : Object.values(error.response.data.error)[0]);
      setAlert({ type: "error", message: firstErr || "Failed to add accessory" });
    }
  };

  return (
    <section className="py-[25px] flex flex-col gap-[50px]">
      <div className="main-title">Add Accessories</div>

      {alert && (
        <CustomAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div>
        <form onSubmit={handleSubmit}>
          {/* Row 1 */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Accessories Name (English)</label>
              <input
                type="text"
                value={accessoriesNameE}
                onChange={(e) => setAccessoriesNameE(e.target.value)}
                placeholder="Accessories Name (English)"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              />
            </div>
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Accessories Name (Arabic)</label>
              <input
                type="text"
                value={accessoriesNameA}
                onChange={(e) => setAccessoriesNameA(e.target.value)}
                placeholder="Accessories Name (Arabic)"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              />
            </div>
          </div>

          {/* Row 2: Category / Brand / Price */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col gap-[15px] w-1/3">
              <label className="text-black capitalize">Select Accessories Category</label>
              <select
                value={selectedCategoryId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedCategoryId(value);
                  const selectedCat = categoriesOptions.find(
                    (cat) => String(cat.accessories_category_id) === String(value)
                  );
                  if (selectedCat) setSelectedCategoryName(selectedCat.accessories_category_name_E);
                }}
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              >
                <option value="" disabled>
                  Select Category
                </option>
                {categoriesOptions.map((cat) => (
                  <option key={cat.accessories_category_id} value={cat.accessories_category_id}>
                    {cat.accessories_category_name_E}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-[15px] w-1/3">
              <label className="text-black capitalize">Select Brand Name</label>
              <select
                value={selectedBrandId}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedBrandId(value);
                  const selectedBrand = brandsOptions.find(
                    (brand) => String(brand.brand_id) === String(value)
                  );
                  if (selectedBrand) setSelectedBrandName(selectedBrand.brand_name);
                }}
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              >
                <option value="" disabled>
                  Select Brand
                </option>
                {brandsOptions.map((brand) => (
                  <option key={brand.brand_id} value={brand.brand_id}>
                    {brand.brand_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-[15px] w-1/3">
              <label className="text-black capitalize">Accessories Price</label>
              <input
                type="text"
                value={accessoriesPrice}
                onChange={(e) => setAccessoriesPrice(e.target.value)}
                placeholder="Price"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              />
            </div>
          </div>

          {/* Row 3 */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Accessories Price Discount</label>
              <input
                type="text"
                value={accessoriesPriceDiscount}
                onChange={(e) => setAccessoriesPriceDiscount(e.target.value)}
                placeholder="Price Discount"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              />
            </div>
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Accessories Stock Available</label>
              <input
                type="text"
                value={accessoriesStockAvailable}
                onChange={(e) => setAccessoriesStockAvailable(e.target.value)}
                placeholder="Stock Available"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Accessories Tag</label>
              <input
                type="text"
                value={accessoriesTag}
                onChange={(e) => setAccessoriesTag(e.target.value)}
                placeholder="Accessories Tag"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black"
              />
            </div>
          </div>

          {/* Row 5 */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Features (English)</label>
              <textarea
                value={accessoriesFeaturesE}
                onChange={(e) => setAccessoriesFeaturesE(e.target.value)}
                placeholder="Features in English"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black h-[200px]"
              />
            </div>
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Features (Arabic)</label>
              <textarea
                value={accessoriesFeaturesA}
                onChange={(e) => setAccessoriesFeaturesA(e.target.value)}
                placeholder="Features in Arabic"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black h-[200px]"
              />
            </div>
          </div>

          {/* Row 6 */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Description (English)</label>
              <textarea
                value={accessoriesDescE}
                onChange={(e) => setAccessoriesDescE(e.target.value)}
                placeholder="Description in English"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black h-[200px]"
              />
            </div>
            <div className="flex flex-col gap-[15px] w-1/2">
              <label className="text-black capitalize">Description (Arabic)</label>
              <textarea
                value={accessoriesDescA}
                onChange={(e) => setAccessoriesDescA(e.target.value)}
                placeholder="Description in Arabic"
                className="px-[15px] py-[7px] border-2 border-[#1485a6] rounded bg-[#eeeeee6e] text-black h-[200px]"
              />
            </div>
          </div>

          {/* Row 7: Colors (ديناميكي مثل البرودكت) */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col w-full">
              <label className="text-black capitalize">Colors</label>

              <div className="flex flex-col gap-3 p-4 bg-gray-100 border-2 rounded border-[#1485a6]">
                <div className="flex flex-wrap gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Color name (e.g. Midnight Blue)"
                    value={colorName}
                    onChange={(e) => setColorName(e.target.value)}
                    onKeyDown={handleColorNameKeyDown}
                    className="flex-1 min-w-[220px] px-3 py-2 border rounded"
                  />
                  <input
                    type="color"
                    title="Color HEX (optional)"
                    value={colorHex}
                    onChange={(e) => setColorHex(e.target.value)}
                    className="w-[48px] h-[40px] border rounded cursor-pointer"
                  />
                  <button
                    type="button"
                    onClick={addColor}
                    className="px-3 py-2 bg-[#1485a6] text-white rounded hover:opacity-90"
                  >
                    Add Color
                  </button>
                </div>

                {accessoriesColor.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {accessoriesColor.map((name) => {
                      const obj =
                        colorsWithHex.find(
                          (c) => c.name.toLowerCase() === name.toLowerCase()
                        ) || { name, hex: "" };
                      return (
                        <div
                          key={name}
                          className="flex items-center gap-2 bg-white border rounded-full px-3 py-1 shadow-sm"
                        >
                          <span
                            className="inline-block w-4 h-4 rounded-full border"
                            style={{ backgroundColor: obj.hex || "transparent" }}
                            title={obj.hex || "No HEX"}
                          />
                          <span className="text-sm font-medium">{name}</span>
                          <input
                            type="color"
                            value={obj.hex || "#000000"}
                            onChange={(e) => updateColorHex(name, e.target.value)}
                            className="w-[32px] h-[24px] border rounded cursor-pointer"
                            title="Change HEX"
                          />
                          <button
                            type="button"
                            onClick={() => removeColor(name)}
                            className="text-red-600 hover:text-red-700 text-sm font-semibold"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No colors added yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Row 8: Images */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col items-center w-1/2">
              <label
                htmlFor="main-image-upload"
                className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] flex items-center justify-center rounded"
              >
                {mainPreview ? (
                  <img
                    src={mainPreview}
                    alt="Main Preview"
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  "Main Image"
                )}
              </label>
              <input
                id="main-image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleMainImageChange}
              />
            </div>

            <div className="flex flex-col items-center w-1/2">
              <label
                htmlFor="additional-images-upload"
                className="cursor-pointer w-full h-[350px] border-2 border-dashed border-[#1485a6] bg-[#eeeeee6e] flex items-center justify-center rounded"
              >
                {additionalPreviews.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2 w-full h-full p-2 overflow-y-auto">
                    {additionalPreviews.map((preview, index) => (
                      <img
                        key={index}
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-[80px] h-[80px] object-cover rounded"
                      />
                    ))}
                  </div>
                ) : (
                  "Upload Additional Images"
                )}
              </label>
              <input
                id="additional-images-upload"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleAdditionalImagesChange}
              />
            </div>
          </div>

          {/* Row 9: Models (filtered by brand) */}
          <div className="flex gap-[10px] w-full">
            <div className="flex flex-col w-full">
              <label className="text-black capitalize">Select Accessories Models</label>
              <div className="p-4 bg-gray-100 border-2 border-[#1485a6] rounded">
                {!selectedBrandId ? (
                  <div className="text-sm text-gray-500">
                    Please select a <span className="font-semibold">Brand</span> first to see its models.
                  </div>
                ) : filteredModels.length === 0 ? (
                  <div className="text-sm text-gray-500">No models found for this brand.</div>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {filteredModels.map((model) => (
                      <label key={model.model_id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          value={model.model_id}
                          checked={selectedModels.includes(model.model_id)}
                          onChange={(e) => handleModelChange(e, model.model_id)}
                          className="h-5 w-5 accent-blue-500 border-gray-300 rounded"
                        />
                        {model.model_name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button type="submit" className="btn flex items-center gap-2 w-full justify-center">
            <AddIcon />
            <span>Add Accessory</span>
          </button>
        </form>
      </div>
    </section>
  );
}
